import Header from "../../../components/feature/Header";
import Footer from "../../../components/feature/Footer";
import SEO from "../../../utils/seo";
import { Helmet } from "react-helmet";

const BurnsideServiceArea = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Starlight Motors Mobile Detailing - Burnside",
    "description": "Professional mobile car detailing service in Burnside, VIC. Full interior & exterior detailing, ceramic coating, and paint correction delivered to your location.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Burnside",
      "addressRegion": "VIC",
      "postalCode": "3023",
      "addressCountry": "AU"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": -37.7333,
      "longitude": 144.7500
    },
    "telephone": "0491121950",
    "url": `${import.meta.env.VITE_SITE_URL || 'https://starlightmotors.com.au'}/service-areas/burnside`,
    "priceRange": "$170-$600",
    "areaServed": {
      "@type": "City",
      "name": "Burnside, VIC"
    }
  };

  const services = [
    {
      title: "Full Interior & Exterior Detail",
      price: "$250",
      originalPrice: "$280",
      features: ["Complete exterior wash & wax", "Interior deep clean", "Leather conditioning", "Window cleaning", "Tire shine"]
    },
    {
      title: "Interior Only Detail",
      price: "$180",
      originalPrice: "$210",
      features: ["Deep vacuum", "Leather treatment", "Dashboard cleaning", "Seat protection", "Interior windows"]
    },
    {
      title: "Exterior Only Detail",
      price: "$120",
      originalPrice: "$150",
      features: ["Exterior wash", "Paint protection wax", "Wheel cleaning", "Tire shine", "Trim restoration"]
    },
    {
      title: "Ceramic Coating",
      price: "from $600",
      features: ["Paint correction", "Premium coating", "2-5 year protection", "Enhanced gloss", "Easy maintenance"]
    }
  ];

  return (
    <>
      <SEO
        title="Mobile Car Detailing Burnside | Professional Auto Detailing Service"
        description="Premium mobile car detailing in Burnside, VIC. Starlight Motors brings professional interior & exterior detailing, ceramic coating, and paint correction to your Burnside driveway. Book today for showroom results."
        keywords="mobile car detailing Burnside, car detailing Burnside VIC, mobile auto detailing Burnside, ceramic coating Burnside, car wash Burnside"
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://starlightmotors.com.au'}/service-areas/burnside`}
        structuredData={structuredData}
      />
      <Helmet>
        <script>
          {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-PTMBTSXR');`}
        </script>
      </Helmet>
      <div className="min-h-screen bg-white">
        <Header />
        <main>
          <section className="relative py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
            <div className="relative max-w-7xl mx-auto px-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                <strong>Mobile Car Detailing Burnside</strong>
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl">
                Premium mobile car detailing services in Burnside. Professional automotive care delivered to your location.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:0491121950" className="inline-flex items-center justify-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-line mr-2 text-xl"></i>Call 0491 121 950
                </a>
                <a href="/#contact" className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
                  Get Free Quote
                </a>
              </div>
            </div>
          </section>

          <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid lg:grid-cols-2 gap-12 mb-16">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                    <strong>Professional Car Detailing in Burnside</strong>
                  </h2>
                  <p className="text-lg text-gray-700 mb-6">
                    Starlight Motors brings professional mobile car detailing services to Burnside residents and businesses. Whether you're in the residential areas around Burnside or near local amenities, we deliver premium car detailing services that exceed expectations while saving you time and effort with our convenient mobile service.
                  </p>
                  <p className="text-lg text-gray-700 mb-6">
                    Our mobile car detailing service in Burnside includes comprehensive interior and exterior detailing, advanced ceramic coating applications, paint correction, and specialized automotive treatments. We use professional-grade products and equipment to ensure your vehicle receives the highest quality care possible, all at your convenient Burnside location.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <i className="ri-map-pin-line text-blue-600 text-2xl mb-2"></i>
                      <h4 className="font-bold text-gray-900 text-sm mb-1"><strong>Local Burnside Service</strong></h4>
                      <p className="text-xs text-gray-600">Trusted by residents for 3+ years</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <i className="ri-star-line text-blue-600 text-2xl mb-2"></i>
                      <h4 className="font-bold text-gray-900 text-sm mb-1"><strong>Premium Quality</strong></h4>
                      <p className="text-xs text-gray-600">Professional-grade products</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <i className="ri-car-line text-blue-600 text-2xl mb-2"></i>
                      <h4 className="font-bold text-gray-900 text-sm mb-1"><strong>Fully Mobile</strong></h4>
                      <p className="text-xs text-gray-600">We come to your location</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <i className="ri-shield-check-line text-blue-600 text-2xl mb-2"></i>
                      <h4 className="font-bold text-gray-900 text-sm mb-1"><strong>Guaranteed Results</strong></h4>
                      <p className="text-xs text-gray-600">100% satisfaction guarantee</p>
                    </div>
                  </div>
                </div>
                <div>
                  <img 
                    src="https://public.readdy.ai/ai/img_res/c2c5f60775f726382794c8296a1dcfd8.jpg"
                    alt="Mobile car detailing in Burnside"
                    title="Professional car detailing Burnside - Starlight Motors"
                    className="w-full h-full object-cover object-top rounded-2xl shadow-2xl"
                  />
                </div>
              </div>

              <div className="mb-16">
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
                  <strong>Burnside Car Detailing Services</strong>
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {services.map((service, index) => (
                    <div key={index} className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-blue-600 hover:shadow-lg transition-all">
                      <div className="flex justify-between items-start mb-4">
                        <h4 className="text-lg font-bold text-gray-900"><strong>{service.title}</strong></h4>
                        <div className="text-right ml-2">
                          {service.originalPrice && (
                            <div className="text-xs text-gray-400 line-through">{service.originalPrice}</div>
                          )}
                          <span className="text-blue-600 font-bold text-lg whitespace-nowrap">{service.price}</span>
                        </div>
                      </div>
                      {service.originalPrice && (
                        <div className="mb-3 text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded">
                          Save $30!
                        </div>
                      )}
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start text-sm text-gray-600">
                            <i className="ri-check-line text-blue-600 mr-2 flex-shrink-0 mt-0.5"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 md:p-12 text-center">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  <strong>Complete Coverage Across Burnside, VIC 3023</strong>
                </h3>
                <p className="text-lg text-gray-700 mb-6 max-w-3xl mx-auto">
                  We service all areas of Burnside including all residential neighborhoods and surrounding streets. Our mobile car detailing service brings professional automotive care directly to your Burnside driveway, eliminating the need to visit a car wash and saving you valuable time.
                </p>
                <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto">
                  From routine maintenance details to premium ceramic coating protection, Starlight Motors is Burnside's trusted partner for mobile car detailing. Experience the convenience of professional car care without leaving home. Book your mobile car detailing service today and discover why Burnside residents choose Starlight Motors for exceptional automotive care.
                </p>
                <a href="/#contact" className="inline-flex items-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap">
                  Book Burnside Detailing
                </a>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default BurnsideServiceArea;
